// src/utils/fieldEditors.ts
//
// The editable-field-path derivation for the structured-fields editors (Story
// 4.5, AC 2/5/6). Like fieldDisplay.ts's label map, this is a LOCAL pure map, NOT
// a re-import of the agent's fieldSchemas module into the browser build (AD-11
// keeps supabase/functions/ out of the Vite graph). AD-14's single-source rule is
// enforced instead by the Vitest PARITY TEST (fieldDisplay.test.ts) that imports
// the import-clean fieldSchemas.ts on the Node side and asserts these paths cover
// EXACTLY the module's canonical keys + every per-type extras key — drift fails
// CI without a risky cross-root runtime import.
//
// fieldDisplay.ts owns LABELS; this owns EDITABILITY (which paths a given ticket
// type may correct). The two are parity-tested against the same source.

// The four canonical routing attributes, as their structured_fields keys. NOTE
// the region key is `region_id` (the stored yak_regions uuid), NOT `region`
// (3.4/AD-14's matcher vocabulary) — the panel resolves the id to a name for
// display, but corrections address the id. The dotted `field` convention (4.2)
// records exactly these keys.
export const CANONICAL_FIELD_PATHS = [
  'brand',
  'region_id',
  'channel',
  'payer',
] as const

// Per-type `extras` interiors (3.6 Decision 9, frozen at pilot) — a MIRROR of
// fieldSchemas.ts EXTRAS_BY_TYPE. formulary_status / out_of_pocket / other carry
// no extras. The parity test pins this to the module; the SQL function mirrors
// the same interiors server-side. Update all three in lockstep.
const EXTRAS_KEYS_BY_TYPE: Record<string, readonly string[]> = {
  formulary_status: [],
  rejected_rx: ['prescriber', 'denial_reason', 'pa_submitted'],
  out_of_pocket: [],
  pharmacy_wholesaler: ['pharmacy_name', 'wholesaler', 'npi', 'pharmacy_phone'],
  other: [],
}

// The full set of correctable field paths for a ticket's question type: the
// canonical four + the type's `extras.<key>` paths. Byte-identical to the schema
// module (the parity test enforces it). An unknown type yields just the canonical
// four (the four are type-independent; extras are the only per-type addition).
export function editableFieldPathsForType(questionType: string): string[] {
  const extras = EXTRAS_KEYS_BY_TYPE[questionType] ?? []
  return [...CANONICAL_FIELD_PATHS, ...extras.map(k => `extras.${k}`)]
}

// The four canonical keys are ROUTING attributes (brand/region/channel/payer) —
// correcting one does not re-run assignment (FR-37), so the editor shows the
// "use Reassign" note in place. Extras are never routing attributes.
export function isRoutingAttribute(path: string): boolean {
  return (CANONICAL_FIELD_PATHS as readonly string[]).includes(path)
}

// Read the current stored value at a field path (top-level canonical key or a
// dotted `extras.<key>` path), tolerant of a missing `extras` object. Returns the
// raw string to prefill the editor, or null when absent/blank/non-string. The
// region row stores a uuid here — the panel resolves the display name separately.
export function currentFieldValue(
  structuredFields: Record<string, unknown> | null | undefined,
  path: string
): string | null {
  const sf = structuredFields ?? {}
  let raw: unknown
  if (path.startsWith('extras.')) {
    const key = path.slice('extras.'.length)
    const extras = sf['extras']
    raw =
      extras && typeof extras === 'object' && !Array.isArray(extras)
        ? (extras as Record<string, unknown>)[key]
        : undefined
  } else {
    raw = sf[path]
  }
  return typeof raw === 'string' && raw.trim() !== '' ? raw : null
}
